import { CheckCircle2Icon, Loader2Icon, Mail, ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { userService } from '../services/userService';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const runningEffect = useRef<boolean>(false);

  const [verifying, setVerifying] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [verified, setVerified] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    if (runningEffect.current) return;
    runningEffect.current = true;

    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');

    if (emailParam) {
      setEmail(emailParam);
    }

    // If no token, this is the "please verify your email" page
    if (!token) {
      setVerifying(false);
      return;
    }

    // If token is present, verify the email
    setVerifying(true);
    userService
      .verifyEmail(token)
      .then((response) => {
        console.log('Email verified successfully:', response);
        if (response.success) {
          setVerified(true);
        } else {
          setError(response.message || 'Failed to verify email');
        }
      })
      .catch((error) => {
        console.log('error:', error);
        setError(error.message || 'Failed to verify email');
      })
      .finally(() => setVerifying(false));
  }, []);

  useEffect(() => {
    if (verified && !verifying) {
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [verified, verifying, navigate]);

  // Show "Please verify your email" page when no token
  if (!verifying && !verified && !error) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}>
        <div className={`max-w-md w-full p-8 rounded-xl shadow-xl ${
          theme === 'dark'
            ? 'bg-gray-800/90 border border-gray-700/50'
            : 'bg-white/95 border border-gray-200/50'
        }`}>
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
              <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>

            <h2 className={`text-2xl font-bold mb-2 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Verify Your Email
            </h2>

            <p className={`text-sm mb-6 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              We've sent a verification link to:
            </p>

            {email && (
              <p className={`font-semibold mb-6 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`}>
                {email}
              </p>
            )}

            <div className={`p-4 rounded-lg mb-6 ${
              theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
            }`}>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Please check your inbox and click the verification link to activate your account.
              </p>
            </div>

            <div className={`text-xs space-y-2 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              <p>Didn't receive the email? Check your spam folder.</p>
              <p>The verification link will expire in 24 hours.</p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className={`mt-6 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Back to Login
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show verification in progress or result
  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${
      theme === 'dark'
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
    }`}>
      <div className={`max-w-md w-full p-8 rounded-xl shadow-xl text-center ${
        theme === 'dark'
          ? 'bg-gray-800/90 border border-gray-700/50'
          : 'bg-white/95 border border-gray-200/50'
      }`}>
        <div className="flex justify-center mb-4">
          {verifying ? (
            <Loader2Icon className="w-12 h-12 animate-spin text-blue-500" />
          ) : verified ? (
            <CheckCircle2Icon className="w-12 h-12 text-green-600 fill-green-100 dark:fill-green-900/30" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <span className="text-red-600 text-2xl">✕</span>
            </div>
          )}
        </div>

        <h2 className={`text-xl font-bold mb-2 ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          {verifying
            ? 'Verifying Your Email...'
            : verified
            ? 'Email Verified Successfully!'
            : 'Verification Failed'}
        </h2>

        <p className={`text-sm ${
          verifying
            ? theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            : verified
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        }`}>
          {verifying
            ? 'Please wait while we verify your email address...'
            : verified
            ? 'Redirecting to login page...'
            : error || 'The verification link is invalid or has expired.'}
        </p>

        {!verifying && !verified && (
          <button
            onClick={() => navigate('/login')}
            className={`mt-6 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Back to Login
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
